import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { apiGet, apiPost, apiPatch } from '../../lib/api-client';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import {
  Table,
  TableHeader,
  TableBody,
  TableRow,
  TableHead,
  TableCell,
} from '../../components/ui/table';
import { Badge } from '../../components/ui/badge';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';

interface CompanyItem {
  id: string;
  name: string;
  code: string;
  status: 'active' | 'inactive';
  hasApiConfig: boolean;
  lastSyncAt: string | null;
  createdAt: string;
}

function AddCompanyDialog({ onClose, onCreated }: { onClose: () => void; onCreated: () => void }) {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const [form, setForm] = useState({
    name: '',
    code: '',
    apiUrl: '',
    apiUsername: '',
    apiPassword: '',
  });
  const [serverError, setServerError] = useState<string | null>(null);

  const createMutation = useMutation({
    mutationFn: (input: typeof form) => apiPost<CompanyItem>('/admin/companies', input),
    onSuccess: (res) => {
      if (res.ok) {
        queryClient.invalidateQueries({ queryKey: ['admin', 'companies'] });
        onCreated();
        onClose();
      } else {
        setServerError(res.error.message);
      }
    },
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setServerError(null);
    createMutation.mutate(form);
  };

  const handleChange = (field: string) => (e: React.ChangeEvent<HTMLInputElement>) => {
    setForm((prev) => ({ ...prev, [field]: e.target.value }));
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
      <div className="mx-4 w-full max-w-md rounded-lg bg-white p-6 shadow-xl dark:bg-gray-800">
        <h3 className="mb-4 text-lg font-semibold text-gray-900 dark:text-gray-100">
          {t('company.addCompany')}
        </h3>
        {serverError && (
          <div className="mb-4 rounded-md border border-red-200 bg-red-50 p-3 text-sm text-red-700 dark:border-red-800 dark:bg-red-950 dark:text-red-300">
            {serverError}
          </div>
        )}
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="mb-1 block text-sm font-medium text-gray-700 dark:text-gray-300">
              {t('company.name')}
            </label>
            <Input value={form.name} onChange={handleChange('name')} required />
          </div>
          <div>
            <label className="mb-1 block text-sm font-medium text-gray-700 dark:text-gray-300">
              {t('company.code')}
            </label>
            <Input
              value={form.code}
              onChange={handleChange('code')}
              placeholder="e.g. my-company"
              required
            />
          </div>
          <div>
            <label className="mb-1 block text-sm font-medium text-gray-700 dark:text-gray-300">
              {t('company.apiUrl')}
            </label>
            <Input
              type="url"
              value={form.apiUrl}
              onChange={handleChange('apiUrl')}
              placeholder="https://company-api.pgway.work"
              required
            />
          </div>
          <div>
            <label className="mb-1 block text-sm font-medium text-gray-700 dark:text-gray-300">
              {t('company.apiUsername')}
            </label>
            <Input value={form.apiUsername} onChange={handleChange('apiUsername')} required />
          </div>
          <div>
            <label className="mb-1 block text-sm font-medium text-gray-700 dark:text-gray-300">
              {t('company.apiPassword')}
            </label>
            <Input
              type="password"
              value={form.apiPassword}
              onChange={handleChange('apiPassword')}
              required
            />
          </div>
          <div className="flex gap-2 pt-2">
            <Button type="submit" disabled={createMutation.isPending} className="flex-1">
              {createMutation.isPending ? t('common.loading') : t('company.addCompany')}
            </Button>
            <Button type="button" variant="ghost" onClick={onClose}>
              {t('common.cancel')}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
}

export function Companies() {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const [showAdd, setShowAdd] = useState(false);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);

  const { data, isLoading, error } = useQuery({
    queryKey: ['admin', 'companies'],
    queryFn: () => apiGet<{ items: CompanyItem[]; total: number }>('/admin/companies'),
  });

  const toggleMutation = useMutation({
    mutationFn: ({ id, status }: { id: string; status: string }) =>
      apiPatch(`/admin/companies/${id}`, { status }),
    onSuccess: (res) => {
      if (res.ok) queryClient.invalidateQueries({ queryKey: ['admin', 'companies'] });
    },
  });

  const companies = data?.ok ? data.data.items : [];

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-end">
        <Button onClick={() => setShowAdd(true)}>{t('company.addCompany')}</Button>
      </div>

      {successMessage && (
        <div className="rounded-md border border-green-200 bg-green-50 p-3 text-sm text-green-700 dark:border-green-800 dark:bg-green-950 dark:text-green-300">
          {successMessage}
        </div>
      )}

      <Card>
        <CardHeader>
          <CardTitle>{t('company.title')}</CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          {isLoading && <div className="p-6 text-gray-400">{t('common.loading')}</div>}
          {error && <div className="p-6 text-red-600">{t('common.error')}</div>}
          {!isLoading && companies.length === 0 && (
            <div className="p-6 text-gray-400">{t('common.noData')}</div>
          )}
          {companies.length > 0 && (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>{t('company.name')}</TableHead>
                  <TableHead>{t('company.code')}</TableHead>
                  <TableHead>{t('sellers.status')}</TableHead>
                  <TableHead>{t('admin.apiStatus')}</TableHead>
                  <TableHead>{t('sellers.actions')}</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {companies.map((co) => (
                  <TableRow key={co.id}>
                    <TableCell className="font-medium">{co.name}</TableCell>
                    <TableCell className="font-mono text-sm">{co.code}</TableCell>
                    <TableCell>
                      <Badge variant={co.status === 'active' ? 'success' : 'danger'}>
                        {t(`common.${co.status}`)}
                      </Badge>
                    </TableCell>
                    <TableCell>
                      {co.hasApiConfig ? (
                        <Badge variant="success">{t('admin.configured')}</Badge>
                      ) : (
                        <Badge variant="warning">{t('admin.notConfigured')}</Badge>
                      )}
                    </TableCell>
                    <TableCell>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => {
                          const next = co.status === 'active' ? 'inactive' : 'active';
                          if (window.confirm(t('common.confirm'))) {
                            toggleMutation.mutate({ id: co.id, status: next });
                          }
                        }}
                      >
                        {co.status === 'active' ? t('common.inactive') : t('common.active')}
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {showAdd && (
        <AddCompanyDialog
          onClose={() => setShowAdd(false)}
          onCreated={() => setSuccessMessage(t('company.created'))}
        />
      )}
    </div>
  );
}
